import { useState, useCallback, useEffect, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowRight, Save, Plus, Trash2, GripVertical,
  Settings, Eye, Upload, Layers, GitBranch, CheckCircle2, AlertCircle, History,
  ChevronDown, ChevronUp, ShieldAlert, Sparkles, Bug,
} from 'lucide-react'
import { AssistantPanel } from '@/features/ai-assistant'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { supabase } from '@/lib/supabase/client'
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, type DragEndEvent, DragOverlay,
} from '@dnd-kit/core'
import {
  SortableContext, sortableKeyboardCoordinates, useSortable,
  verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { useIsDev } from '@/hooks/use-is-dev'
import {
  fetchFormVersion, saveFormDraft, makeNewField, DraftConflictError,
  analyzeFormChangeImpact, publishFormVersion, fetchFormVersionHistory,
  type FormDefinition, type FieldDefinition, type FieldType,
  type ConditionalRule, type ImpactResult,
} from '../api/form-builder-api'
import { computeDiff, diffItemId } from '../lib/diff-engine'
import { classifyAll, type ClassifiedChange } from '../lib/classify-changes'
import {
  validateDefinition,
  buildDefaultDecisions,
  type ValidationError,
} from './form-editor-validation'
import { VersionHistoryRow } from './version-history-row'
import { FieldPalette, FIELD_TYPES } from '../components/field-palette'
import { FieldEditorPanel } from '../components/field-editor-panel'
import { FormSettingsPanel } from '../components/form-settings-panel'
import { RulesSection } from '../components/rules-section'
import { FormPreviewPane } from '../components/form-preview-pane'
import { ResizableSplit } from '@/components/shared/resizable-split'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import { cn } from '@/lib/utils/cn'

// ─── Field type icons ─────────────────────────────────────────────────────────

const FIELD_TYPE_ICONS: Record<string, string> = {
  text: '🔤', textarea: '📝', number: '🔢', date: '📅', time: '⏰',
  datetime: '🕐', single_select: '⊙', multi_select: '☑', boolean: '✓',
  file: '📎', image: '🖼', calculated: '🧮', repeatable_array: '🔁',
}

async function fetchAffectedRecordsCount(formId: string): Promise<number> {
  const { count } = await supabase
    .from('submissions')
    .select('record_id', { count: 'exact', head: true })
    .eq('form_id', formId)
    .eq('is_deleted', false)
  return count ?? 0
}

// ─── Sortable field row ────────────────────────────────────────────────────────

interface SortableFieldProps {
  field: FieldDefinition
  isSelected: boolean
  onSelect: () => void
  onDelete: () => void
}

function SortableField({ field, isSelected, onSelect, onDelete }: SortableFieldProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.field_key,
  })
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors group',
        isSelected
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-primary/40 hover:bg-muted/30'
      )}
      onClick={onSelect}
    >
      <button
        {...attributes}
        {...listeners}
        className="text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <span className="text-base shrink-0">{FIELD_TYPE_ICONS[field.field_type] ?? '📄'}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">
          {field.label.fa || '(بدون عنوان)'}
        </p>
        <p className="text-[11px] font-mono text-muted-foreground">{field.field_key}</p>
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {field.is_required && <span className="text-[10px] text-red-400">*</span>}
        <button
          onClick={(e) => { e.stopPropagation(); onDelete() }}
          className="text-muted-foreground hover:text-destructive transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  )
}

// ─── Fields Section ───────────────────────────────────────────────────────────

interface FieldsSectionProps {
  definition: FormDefinition
  selectedFieldKey: string | null
  onSelectField: (key: string | null) => void
  onAddField: (type: FieldType) => void
  onRequestDeleteField: (key: string) => void
  onUpdateField: (updated: FieldDefinition) => void
  onDragEnd: (event: DragEndEvent) => void
  sensors: ReturnType<typeof useSensors>
  onOpenRule?: (ruleId: string) => void
  publishedFieldKeys?: Set<string>
}

function FieldsSection({
  definition, selectedFieldKey, onSelectField,
  onAddField, onRequestDeleteField, onUpdateField, onDragEnd, sensors,
  onOpenRule, publishedFieldKeys,
}: FieldsSectionProps) {
  const selectedField = definition.fields.find((f) => f.field_key === selectedFieldKey)
  const [activeId, setActiveId] = useState<string | null>(null)

  const activePaletteType = activeId?.startsWith('palette:') ? activeId.replace('palette:', '') : null
  const activeField = activeId && !activeId.startsWith('palette:')
    ? definition.fields.find(f => f.field_key === activeId)
    : null

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={(e) => setActiveId(e.active.id.toString())}
      onDragEnd={(e) => { setActiveId(null); onDragEnd(e) }}
      onDragCancel={() => setActiveId(null)}
    >
      <ResizableSplit
        storageKey="form-editor-palette-split"
        defaultLeftPct={18}
        minPct={12}
        className="flex-1"
        left={
          <div className="h-full overflow-y-auto bg-card">
            <FieldPalette onAddField={onAddField} />
          </div>
        }
        right={
          <ResizableSplit
            storageKey="form-editor-list-inspector-split"
            defaultLeftPct={58}
            minPct={20}
            className="h-full"
            left={
              <div className="h-full overflow-y-auto p-5">
          {definition.fields.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center gap-3">
              <Layers className="w-10 h-10 text-muted-foreground/40" />
              <p className="text-muted-foreground text-sm">
                از پالت سمت راست، نوع فیلد را انتخاب کنید
              </p>
            </div>
          ) : (
            <SortableContext
              items={definition.fields.map((f) => f.field_key)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {definition.fields.map((field) => (
                  <SortableField
                    key={field.field_key}
                    field={field}
                    isSelected={selectedFieldKey === field.field_key}
                    onSelect={() => onSelectField(field.field_key)}
                    onDelete={() => onRequestDeleteField(field.field_key)}
                  />
                ))}
              </div>
            </SortableContext>
          )}

          {/* Add field dropdown */}
          {definition.fields.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="mt-3 w-full flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-border py-2 text-xs text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors">
                  <Plus className="w-3.5 h-3.5" />
                  افزودن فیلد
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-44">
                {FIELD_TYPES.map((ft) => (
                  <DropdownMenuItem
                    key={ft.type}
                    onClick={() => onAddField(ft.type as FieldType)}
                  >
                    <span className="me-2 text-base">{ft.icon}</span>
                    {ft.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
              </div>
            }
            right={
              <div className="h-full overflow-y-auto bg-card">
                {selectedField ? (
                  <FieldEditorPanel
                    field={selectedField}
                    allFields={definition.fields}
                    conditionalRules={definition.conditional_rules}
                    onChange={onUpdateField}
                    onClose={() => onSelectField(null)}
                    onOpenRule={onOpenRule}
                    publishedFieldKeys={publishedFieldKeys}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center h-full p-6 text-center gap-3">
                    <Settings className="w-8 h-8 text-muted-foreground/40" />
                    <p className="text-sm text-muted-foreground">
                      برای ویرایش خصوصیات فیلد، روی آن کلیک کنید
                    </p>
                  </div>
                )}
              </div>
            }
          />
        }
      />

      {/* Drag overlay */}
      <DragOverlay dropAnimation={null}>
        {activePaletteType && (
          <div className="flex items-center gap-2 rounded-lg border border-primary bg-card shadow-lg px-3 py-2 cursor-grabbing opacity-90">
            <span className="text-base">{FIELD_TYPE_ICONS[activePaletteType]}</span>
            <span className="text-sm font-medium text-foreground">
              {FIELD_TYPES.find(ft => ft.type === activePaletteType)?.label}
            </span>
          </div>
        )}
        {activeField && (
          <div className="flex items-center gap-2 rounded-lg border border-primary bg-card shadow-lg px-3 py-2 cursor-grabbing opacity-90">
            <GripVertical className="w-4 h-4 text-muted-foreground" />
            <span className="text-base">{FIELD_TYPE_ICONS[activeField.field_type]}</span>
            <span className="text-sm font-medium text-foreground">
              {activeField.label.fa || '(بدون عنوان)'}
            </span>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  )
}

// ─── Section content (extracted so the editor can render it either standalone
//     or as the left pane of a resizable split with live preview on the right) ──

type Section = 'fields' | 'rules' | 'settings' | 'history'

interface SectionContentProps {
  section: Section
  definition: FormDefinition
  selectedFieldKey: string | null
  onSelectField: (k: string | null) => void
  onAddField: (t: FieldType) => void
  onRequestDeleteField: (k: string) => void
  onUpdateField: (f: FieldDefinition) => void
  onDragEnd: (e: DragEndEvent) => void
  sensors: ReturnType<typeof useSensors>
  onAddRule: (r: ConditionalRule) => void
  onUpdateRule: (r: ConditionalRule) => void
  onDeleteRule: (id: string) => void
  onReorderRules: (rules: ConditionalRule[]) => void
  projectId: string
  formId: string
  versionHistory: import('../api/form-builder-api').FormVersionRow[] | undefined
  versionHistoryLoading: boolean
  versionHistoryError: unknown
  isDraftOnly: boolean
  onOpenRule: (ruleId: string) => void
  pendingOpenRuleId: string | null
  onPendingOpenRuleConsumed: () => void
  publishedFieldKeys?: Set<string>
  publishedDef?: FormDefinition | null
}

function SectionContent(props: SectionContentProps) {
  const {
    section, definition, selectedFieldKey, onSelectField,
    onAddField, onRequestDeleteField, onUpdateField, onDragEnd, sensors,
    onAddRule, onUpdateRule, onDeleteRule, onReorderRules,
    projectId, formId, versionHistory, versionHistoryLoading, versionHistoryError, isDraftOnly,
    onOpenRule, pendingOpenRuleId, onPendingOpenRuleConsumed,
    publishedFieldKeys, publishedDef,
  } = props

  if (section === 'fields') {
    return (
      <FieldsSection
        definition={definition}
        selectedFieldKey={selectedFieldKey}
        onSelectField={onSelectField}
        onAddField={onAddField}
        onRequestDeleteField={onRequestDeleteField}
        onUpdateField={onUpdateField}
        onDragEnd={onDragEnd}
        sensors={sensors}
        onOpenRule={onOpenRule}
        publishedFieldKeys={publishedFieldKeys}
      />
    )
  }
  if (section === 'rules') {
    return (
      <RulesSection
        definition={definition}
        onAddRule={onAddRule}
        onUpdateRule={onUpdateRule}
        onDeleteRule={onDeleteRule}
        onReorderRules={onReorderRules}
        autoOpenRuleId={pendingOpenRuleId}
        onAutoOpenConsumed={onPendingOpenRuleConsumed}
      />
    )
  }
  if (section === 'settings') {
    return (
      <div className="flex-1 overflow-y-auto max-w-lg mx-auto w-full py-4 px-6">
        {projectId && formId && (
          <FormSettingsPanel formId={formId} projectId={projectId} />
        )}
      </div>
    )
  }
  // history
  return (
    <div className="flex-1 overflow-y-auto py-4 px-6">
      <div className="max-w-2xl mx-auto space-y-3">
        <h2 className="text-sm font-semibold text-foreground">تاریخچه نسخه‌ها</h2>

        {versionHistoryLoading && (
          <p className="text-xs text-muted-foreground text-center py-4">در حال بارگذاری...</p>
        )}
        {!versionHistoryLoading && Boolean(versionHistoryError) && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-xs text-red-400">
            خطا در بارگذاری تاریخچه: {(versionHistoryError as Error)?.message ?? 'خطای ناشناخته'}
          </div>
        )}
        {!versionHistoryLoading && !versionHistoryError && (!versionHistory || versionHistory.length === 0) && (
          <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-6 text-center space-y-1.5">
            <p className="text-sm text-foreground">
              {isDraftOnly
                ? 'این فرم در حالت پیش‌نویس است و هنوز منتشر نشده.'
                : 'هنوز نسخه‌ای منتشر نشده است.'}
            </p>
            <p className="text-[11px] text-muted-foreground">
              {isDraftOnly
                ? 'پس از انتشار نسخه ۱.۰، تاریخچه نسخه‌ها در همین بخش نمایش داده می‌شود.'
                : 'با انتشار اولین نسخه، تاریخچه ایجاد می‌شود.'}
            </p>
          </div>
        )}
        {versionHistory && versionHistory.map((v) => (
          <VersionHistoryRow
            key={v.id}
            row={v}
            currentFields={definition.fields}
            publishedFields={publishedDef?.fields}
          />
        ))}
      </div>
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

type DecisionChoice = 'grandfather' | 'migrate' | 'mark_for_revision'
interface UserDecision {
  choice: DecisionChoice
  migration_mapping: Record<string, string>
}

export function FormEditorPage() {
  const { projectId, formId } = useParams<{ projectId: string; formId: string }>()
  const qc = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const { toast } = useToast()
  const isDev = useIsDev()

  const [definition, setDefinition] = useState<FormDefinition>({
    fields: [],
    conditional_rules: [],
    calculated_fields: [],
  })
  const [draftId, setDraftId] = useState<string | null>(null)
  const [draftVersion, setDraftVersion] = useState<number | null>(null)
  const [formTitle, setFormTitle] = useState('')
  const [currentVersionId, setCurrentVersionId] = useState<string | null>(null)
  const [publishedVersionLabel, setPublishedVersionLabel] = useState<string | null>(null)
  const [publishedFieldKeys, setPublishedFieldKeys] = useState<Set<string>>(new Set())
  const [publishedDef, setPublishedDef] = useState<FormDefinition | null>(null)
  const [selectedFieldKey, setSelectedFieldKey] = useState<string | null>(null)
  const [isDirty, setIsDirty] = useState(false)
  const [draftConflictOpen, setDraftConflictOpen] = useState(false)
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false)
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false)
  const [publishPreview, setPublishPreview] = useState<{
    classified: ClassifiedChange[]
    impactData: ImpactResult[] | null
    affectedCount: number
  } | null>(null)
  const [publishPreviewLoading, setPublishPreviewLoading] = useState(false)
  const [userDecisions, setUserDecisions] = useState<Map<string, UserDecision>>(new Map())
  const [superUserPhrase, setSuperUserPhrase] = useState('')
  const [section, setSection] = useState<Section>('fields')
  const [deletingFieldKey, setDeletingFieldKey] = useState<string | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [assistantOpen, setAssistantOpen] = useState(false)
  const [pendingOpenRuleId, setPendingOpenRuleId] = useState<string | null>(null)
  const [formKey, setFormKey] = useState<string>('')
  const [instanceType, setInstanceType] = useState<'single' | 'repeatable'>('single')

  const openRuleFromField = useCallback((ruleId: string) => {
    setSection('rules')
    setPendingOpenRuleId(ruleId)
  }, [])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const { isLoading, data: formVersionData } = useQuery({
    queryKey: ['form-version', formId],
    queryFn: () => fetchFormVersion(formId!),
    enabled: !!formId,
    staleTime: 0,
    refetchOnMount: 'always',
  })

  const {
    data: versionHistory,
    isLoading: versionHistoryLoading,
    error: versionHistoryError,
  } = useQuery({
    queryKey: ['form-version-history', formId],
    queryFn: () => fetchFormVersionHistory(formId!),
    enabled: !!formId && section === 'history',
  })

  useEffect(() => {
    if (!formVersionData) return
    setDefinition(formVersionData.definition)
    setDraftId(formVersionData.draftId)
    setDraftVersion(formVersionData.draftVersion ?? null)
    setFormTitle(formVersionData.formTitle)
    setCurrentVersionId(formVersionData.currentVersionId ?? null)
    if ('formKey' in formVersionData && formVersionData.formKey) {
      setFormKey(formVersionData.formKey as string)
    }
    if ('instanceType' in formVersionData && formVersionData.instanceType) {
      setInstanceType(formVersionData.instanceType as 'single' | 'repeatable')
    }
    if ('publishedVersionLabel' in formVersionData && formVersionData.publishedVersionLabel) {
      setPublishedVersionLabel(formVersionData.publishedVersionLabel as string)
    }
    if ('publishedFieldKeys' in formVersionData && formVersionData.publishedFieldKeys instanceof Set) {
      setPublishedFieldKeys(formVersionData.publishedFieldKeys as Set<string>)
    }
    if ('publishedDef' in formVersionData && formVersionData.publishedDef) {
      setPublishedDef(formVersionData.publishedDef as FormDefinition)
    }
  }, [formVersionData])

  const updateDefinition = useCallback(
    (updater: (d: FormDefinition) => FormDefinition) => {
      setDefinition((prev) => updater(prev))
      setIsDirty(true)
    },
    []
  )

  const saveMutation = useMutation({
    mutationFn: () => saveFormDraft(formId!, user!.id, definition, draftId, draftVersion ?? undefined),
    onSuccess: (result) => {
      setDraftId(result.id)
      setDraftVersion(result.version)
      setIsDirty(false)
      // Patch the form-version cache so the preview route (and any other consumer)
      // sees the just-saved draft without a refetch race. Refetching here would
      // risk overwriting any edits the user typed during the save round-trip.
      qc.setQueryData(['form-version', formId], (old: unknown) => {
        if (!old || typeof old !== 'object') {
          return {
            draftId: result.id,
            draftVersion: result.version,
            definition,
            isDraft: true,
            formTitle,
            currentVersionId,
            publishedVersionLabel,
            publishedFieldKeys,
            publishedDef,
          }
        }
        return {
          ...(old as Record<string, unknown>),
          draftId: result.id,
          draftVersion: result.version,
          definition,
          isDraft: true,
        }
      })
      toast({ title: 'پیش‌نویس ذخیره شد', duration: 1500 })
    },
    onError: (err) => {
      if (err instanceof DraftConflictError) {
        setDraftConflictOpen(true)
      } else {
        toast({ title: 'خطا در ذخیره پیش‌نویس', description: (err as Error).message, variant: 'destructive' })
      }
    },
  })

  const discardMutation = useMutation({
    mutationFn: async () => {
      if (!draftId) return
      const { error } = await supabase.from('form_drafts').delete().eq('id', draftId)
      if (error) throw error
    },
    onSuccess: () => {
      setDiscardConfirmOpen(false)
      setDraftId(null)
      setDraftVersion(null)
      setIsDirty(false)
      qc.invalidateQueries({ queryKey: ['form-version', formId] })
      toast({ title: 'پیش‌نویس حذف شد', description: 'فرم به نسخه منتشرشده بازگشت.' })
    },
  })

  const hasInstanceTypeChange = publishPreview?.classified.some(
    (c) => c.item.type === 'form_instance_type_changed'
  ) ?? false

  const publishMutation = useMutation({
    mutationFn: async () => {
      const classified = publishPreview?.classified ?? []
      const impactData = publishPreview?.impactData ?? null
      const clientDiff = classified.map((c) => ({
        diffItemId:     diffItemId(c.item),
        classification: c.classification,
      }))

      // Build decisions from user choices (fall back to grandfather for items without explicit choice)
      const decisions = classified
        .filter((c) => {
          if (c.classification === 'breaking') return true
          if (c.classification === 'breaking_pending_data_check') {
            const impact = impactData?.find(
              (d) => d.field_key === (c.item.fieldKey ?? '') && d.change_type === c.item.type
            )
            return (impact?.affected_count ?? 0) > 0
          }
          return false
        })
        .map((c) => {
          const id = diffItemId(c.item)
          const dec = userDecisions.get(id)
          return {
            diffItemId:         id,
            change_type:        c.item.type,
            field_key:          c.item.fieldKey,
            choice:             dec?.choice ?? 'grandfather' as const,
            removed_option_key: c.item.details?.optionKey as string | undefined,
            old_type:           c.item.before as string | undefined,
            new_type:           c.item.after as string | undefined,
            migration_mapping:  dec?.migration_mapping && Object.keys(dec.migration_mapping).length > 0
              ? dec.migration_mapping : undefined,
          }
        })

      const result = await publishFormVersion({
        form_id:            formId!,
        draft_version:      draftVersion ?? 1,
        client_diff:        clientDiff,
        decisions,
        super_user_confirm: hasInstanceTypeChange ? true : undefined,
      })
      return `${result.version_major}.${result.version_minor}`
    },
    onSuccess: (version) => {
      setPublishConfirmOpen(false)
      setUserDecisions(new Map())
      setSuperUserPhrase('')
      setDraftId(null)
      setDraftVersion(null)
      setIsDirty(false)
      qc.invalidateQueries({ queryKey: ['form-groups', projectId] })
      qc.invalidateQueries({ queryKey: ['form-version', formId] })
      qc.invalidateQueries({ queryKey: ['form-version-history', formId] })
      toast({ title: 'فرم منتشر شد', description: `نسخه ${version} با موفقیت ذخیره شد` })
      setSection('history')
    },
    onError: (err: Error) => {
      if (err.message.startsWith('optimistic_lock_conflict')) {
        toast({
          title: 'تعارض نسخه',
          description: 'ادمین دیگری این فرم را منتشر کرد. بارگذاری مجدد؟',
          variant: 'destructive',
        })
      }
    },
  })

  // ── Field operations ──────────────────────────────────────────────────────

  const addField = useCallback(
    (fieldType: FieldType) => {
      const field = makeNewField(fieldType)
      updateDefinition((d) => ({ ...d, fields: [...d.fields, field] }))
      setSelectedFieldKey(field.field_key)
    },
    [updateDefinition]
  )

  const deleteField = useCallback(
    (fieldKey: string) => {
      updateDefinition((d) => ({ ...d, fields: d.fields.filter((f) => f.field_key !== fieldKey) }))
      if (selectedFieldKey === fieldKey) setSelectedFieldKey(null)
      setDeletingFieldKey(null)
    },
    [updateDefinition, selectedFieldKey]
  )

  const updateField = useCallback(
    (updated: FieldDefinition) => {
      updateDefinition((d) => ({
        ...d,
        fields: d.fields.map((f) => (f.field_key === updated.field_key ? updated : f)),
      }))
    },
    [updateDefinition]
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      const activeId = active.id.toString()

      // Palette drop → create new field
      if (activeId.startsWith('palette:')) {
        const fieldType = activeId.replace('palette:', '') as FieldType
        const field = makeNewField(fieldType)
        updateDefinition((d) => {
          if (over && !over.id.toString().startsWith('palette:')) {
            const overIdx = d.fields.findIndex((f) => f.field_key === over.id)
            if (overIdx >= 0) {
              const newFields = [...d.fields]
              newFields.splice(overIdx + 1, 0, field)
              return { ...d, fields: newFields }
            }
          }
          return { ...d, fields: [...d.fields, field] }
        })
        setSelectedFieldKey(field.field_key)
        return
      }

      // Reorder existing fields
      if (!over || active.id === over.id) return
      updateDefinition((d) => {
        const oldIdx = d.fields.findIndex((f) => f.field_key === active.id)
        const newIdx = d.fields.findIndex((f) => f.field_key === over.id)
        return { ...d, fields: arrayMove(d.fields, oldIdx, newIdx) }
      })
    },
    [updateDefinition]
  )

  // ── Rule operations ───────────────────────────────────────────────────────

  // priority is derived from list position; re-stamp on every mutation so the
  // engine's priority-based sort matches the user's drag-and-drop order.
  const restampPriorities = (rules: ConditionalRule[]): ConditionalRule[] =>
    rules.map((r, i) => ({ ...r, priority: (i + 1) * 10 }))

  const addRule = useCallback(
    (rule: ConditionalRule) => {
      updateDefinition((d) => ({
        ...d,
        conditional_rules: restampPriorities([...d.conditional_rules, rule]),
      }))
    },
    [updateDefinition]
  )

  const reorderRules = useCallback(
    (rules: ConditionalRule[]) => {
      updateDefinition((d) => ({
        ...d,
        conditional_rules: restampPriorities(rules),
      }))
    },
    [updateDefinition]
  )

  const updateRule = useCallback(
    (updated: ConditionalRule) => {
      updateDefinition((d) => ({
        ...d,
        conditional_rules: d.conditional_rules.map((r) =>
          r.rule_id === updated.rule_id ? updated : r
        ),
      }))
    },
    [updateDefinition]
  )

  const deleteRule = useCallback(
    (ruleId: string) => {
      updateDefinition((d) => ({
        ...d,
        conditional_rules: restampPriorities(d.conditional_rules.filter((r) => r.rule_id !== ruleId)),
      }))
    },
    [updateDefinition]
  )

  // ── Publish validation ────────────────────────────────────────────────────

  const validationErrors = validateDefinition(definition)

  // Inline diagnostics: calculated fields referencing missing field keys
  const allFieldKeys = useMemo(
    () => new Set(definition.fields.map((f) => f.field_key)),
    [definition.fields]
  )
  const brokenFormulaKeys = useMemo(() => {
    const broken = new Set<string>()
    for (const calc of definition.calculated_fields) {
      const refs = [...(calc.formula.matchAll(/\{([^}]+)\}/g))].map((m) => m[1])
      for (const ref of refs) {
        if (!allFieldKeys.has(ref)) { broken.add(calc.field_key); break }
      }
    }
    return broken
  }, [definition.calculated_fields, allFieldKeys])

  const brokenRuleIds = useMemo(() => {
    const broken = new Set<string>()
    for (const rule of definition.conditional_rules) {
      for (const action of rule.actions) {
        if (!allFieldKeys.has(action.target_field_key)) { broken.add(rule.rule_id); break }
      }
    }
    return broken
  }, [definition.conditional_rules, allFieldKeys])

  const hasBrokenFormulas = brokenFormulaKeys.size > 0
  const hasBlockingErrors = validationErrors.length > 0 || hasBrokenFormulas

  const deletingField = deletingFieldKey
    ? definition.fields.find((f) => f.field_key === deletingFieldKey)
    : null

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <div className="h-8 bg-muted rounded animate-pulse w-48" />
        <div className="h-64 bg-muted rounded-xl animate-pulse" />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="border-b border-border px-6 py-3 shrink-0">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
          <Link to={`/projects/${projectId}/form-builder`} className="hover:text-foreground transition-colors">
            فرم‌ساز
          </Link>
          <ArrowRight className="w-3 h-3 rotate-180" />
          <span className="text-foreground">{formTitle}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <h1 className="text-base font-bold text-foreground">{formTitle}</h1>
            {currentVersionId && !draftId && publishedVersionLabel && (
              <Badge variant="outline" className="text-emerald-400 border-emerald-500/30 bg-emerald-500/10 text-[10px]">
                نسخه {publishedVersionLabel}
              </Badge>
            )}
            {!currentVersionId && !draftId && (
              <Badge variant="outline" className="text-muted-foreground border-border text-[10px]">
                منتشرنشده
              </Badge>
            )}
            {draftId && (
              <Badge variant="outline" className="text-yellow-400 border-yellow-500/30 bg-yellow-500/10 text-[10px]">
                پیش‌نویس {publishedVersionLabel ? `(بر پایه ${publishedVersionLabel})` : ''}
              </Badge>
            )}
            {hasBlockingErrors && (
              <Badge variant="outline" className="text-red-400 border-red-500/30 bg-red-500/10 text-[10px] gap-1">
                <AlertCircle className="w-3 h-3" />
                {validationErrors.length + brokenFormulaKeys.size} خطا
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isDirty && <span className="text-xs text-muted-foreground">ذخیره نشده</span>}
            {draftId && currentVersionId && (
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 text-muted-foreground hover:text-destructive"
                onClick={() => setDiscardConfirmOpen(true)}
              >
                حذف پیش‌نویس
              </Button>
            )}
            {isDev && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 text-amber-400 border-amber-500/30 hover:bg-amber-500/10"
              title="Debug: log form builder state to console"
              onClick={() => {
                const emptyDef: FormDefinition = { fields: [], conditional_rules: [], calculated_fields: [] }
                const diffVsPublished = publishedDef ? computeDiff(publishedDef, definition) : null
                const classifiedDiff = diffVsPublished ? classifyAll(diffVsPublished) : null
                const payload = {
                  ids: { projectId, formId, draftId, draftVersion, currentVersionId, publishedVersionLabel },
                  form: { formTitle, formKey, instanceType, isDirty, section, selectedFieldKey },
                  counts: {
                    fields: definition.fields.length,
                    conditional_rules: definition.conditional_rules.length,
                    calculated_fields: definition.calculated_fields.length,
                  },
                  validation: {
                    hasBlockingErrors,
                    errorCount: validationErrors.length,
                    brokenFormulaKeys: Array.from(brokenFormulaKeys),
                    errors: validationErrors,
                  },
                  definition,
                  publishedDef: publishedDef ?? null,
                  publishedFieldKeys: Array.from(publishedFieldKeys),
                  diffVsPublished,
                  classifiedDiff,
                  emptyDefShape: emptyDef,
                }
                // eslint-disable-next-line no-console
                console.groupCollapsed(
                  `%c[FormBuilder Debug] ${formTitle || formKey || formId} — ${definition.fields.length} fields, ${definition.conditional_rules.length} rules, ${definition.calculated_fields.length} calc`,
                  'color:#f59e0b;font-weight:bold',
                )
                // eslint-disable-next-line no-console
                console.log('ids', payload.ids)
                // eslint-disable-next-line no-console
                console.log('form meta', payload.form)
                // eslint-disable-next-line no-console
                console.log('counts', payload.counts)
                // eslint-disable-next-line no-console
                console.log('validation', payload.validation)
                // eslint-disable-next-line no-console
                console.log('definition (current schema)', payload.definition)
                // eslint-disable-next-line no-console
                console.log('publishedDef', payload.publishedDef)
                // eslint-disable-next-line no-console
                console.log('publishedFieldKeys', payload.publishedFieldKeys)
                // eslint-disable-next-line no-console
                console.log('diff vs published', payload.diffVsPublished)
                // eslint-disable-next-line no-console
                console.log('classified diff', payload.classifiedDiff)
                // eslint-disable-next-line no-console
                console.log('full payload (copy)', payload)
                // eslint-disable-next-line no-console
                console.log('definition JSON:\n' + JSON.stringify(definition, null, 2))
                // eslint-disable-next-line no-console
                console.groupEnd()
                try {
                  navigator.clipboard?.writeText(JSON.stringify(payload, null, 2))
                } catch { /* ignore */ }
              }}
            >
              <Bug className="w-4 h-4" />
              دیباگ
            </Button>
            )}
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => saveMutation.mutate()}>
              <Save className="w-4 h-4" />
              {saveMutation.isPending ? 'ذخیره...' : 'ذخیره'}
            </Button>
            <Button
              variant={previewOpen ? 'default' : 'outline'}
              size="sm"
              className="gap-1.5"
              onClick={() => setPreviewOpen((v) => !v)}
            >
              <Eye className="w-4 h-4" />
              {previewOpen ? 'بستن پیش‌نمایش' : 'پیش‌نمایش'}
            </Button>
            <Button
              variant={assistantOpen ? 'default' : 'outline'}
              size="sm"
              className="gap-1.5"
              onClick={() => setAssistantOpen((v) => !v)}
            >
              <Sparkles className="w-4 h-4" />
              {assistantOpen ? 'بستن دستیار' : 'دستیار هوشمند'}
            </Button>
            <Button
              size="sm"
              className="gap-1.5"
              disabled={hasBlockingErrors || publishPreviewLoading}
              onClick={async () => {
                if (isDirty) await saveMutation.mutateAsync()
                setPublishPreviewLoading(true)
                try {
                  const emptyDef: FormDefinition = { fields: [], conditional_rules: [], calculated_fields: [] }
                  const diffItems = computeDiff(publishedDef ?? emptyDef, definition)
                  const classified = classifyAll(diffItems)

                  // Phase 6: impact analysis for breaking_pending_data_check items
                  const pendingChanges = classified
                    .filter((c) => c.classification === 'breaking_pending_data_check')
                    .map((c) => ({
                      field_key: c.item.fieldKey ?? '',
                      change_type: c.item.type,
                      removed_option_key: c.item.details?.optionKey as string | undefined,
                      old_type: c.item.before as string | undefined,
                      new_type: c.item.after as string | undefined,
                    }))

                  const [impactData, affectedCount] = await Promise.all([
                    pendingChanges.length > 0
                      ? analyzeFormChangeImpact(formId!, pendingChanges)
                      : Promise.resolve([] as ImpactResult[]),
                    fetchAffectedRecordsCount(formId!),
                  ])

                  setPublishPreview({ classified, impactData, affectedCount })

                  // Initialize per-change decisions (default: grandfather)
                  const initDecisions = new Map<string, UserDecision>()
                  for (const c of classified) {
                    const needsDecision =
                      c.classification === 'breaking' ||
                      (c.classification === 'breaking_pending_data_check' &&
                        (impactData.find(
                          (d) => d.field_key === (c.item.fieldKey ?? '') && d.change_type === c.item.type
                        )?.affected_count ?? 0) > 0)
                    if (needsDecision) {
                      initDecisions.set(diffItemId(c.item), { choice: 'grandfather', migration_mapping: {} })
                    }
                  }
                  setUserDecisions(initDecisions)
                  setSuperUserPhrase('')
                  setPublishConfirmOpen(true)
                } finally {
                  setPublishPreviewLoading(false)
                }
              }}
            >
              <Upload className="w-4 h-4" />
              {publishPreviewLoading ? 'در حال بارگذاری...' : 'انتشار'}
            </Button>
          </div>
        </div>
      </div>

      {/* ── Section navigation ────────────────────────────────────────────── */}
      <div className="border-b border-border bg-card px-6 shrink-0">
        <div className="flex gap-0">
          {(
            [
              { id: 'fields', label: 'فیلدها', icon: Layers, count: definition.fields.length },
              { id: 'rules', label: 'قوانین', icon: GitBranch, count: definition.conditional_rules.length },
              { id: 'settings', label: 'تنظیمات', icon: Settings, count: null },
              { id: 'history', label: 'تاریخچه', icon: History, count: null },
            ] as const
          ).map(({ id, label, icon: Icon, count }) => (
            <button
              key={id}
              onClick={() => setSection(id)}
              className={cn(
                'flex items-center gap-1.5 px-4 py-2.5 text-xs border-b-2 transition-colors',
                section === id
                  ? 'border-primary text-primary font-medium'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
              {count !== null && count > 0 && (
                <span className="bg-muted text-muted-foreground rounded-full px-1.5 text-[10px] leading-4">
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Error banners ─────────────────────────────────────────────────── */}
      {(validationErrors.length > 0 || hasBrokenFormulas) && (
        <div className="shrink-0 bg-red-500/10 border-b border-red-500/20 px-6 py-2">
          <ul className="space-y-0.5">
            {validationErrors.map((e, i) => (
              <li key={i} className="text-xs text-red-400 flex items-start gap-1">
                <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                {e.message}
              </li>
            ))}
            {brokenFormulaKeys.size > 0 && (
              <li className="text-xs text-red-400 flex items-start gap-1">
                <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                {brokenFormulaKeys.size} فیلد محاسباتی به فیلد حذف‌شده اشاره دارد — انتشار بلوکه است
              </li>
            )}
          </ul>
        </div>
      )}

      {/* ── Section content + optional side panels (preview / assistant) ──── */}
      {(() => {
        const editorPane = (
          <SectionContent
            section={section}
            definition={definition}
            selectedFieldKey={selectedFieldKey}
            onSelectField={setSelectedFieldKey}
            onAddField={addField}
            onRequestDeleteField={setDeletingFieldKey}
            onUpdateField={updateField}
            onDragEnd={handleDragEnd}
            sensors={sensors}
            onAddRule={addRule}
            onUpdateRule={updateRule}
            onDeleteRule={deleteRule}
            onReorderRules={reorderRules}
            projectId={projectId ?? ''}
            formId={formId ?? ''}
            versionHistory={versionHistory}
            versionHistoryLoading={versionHistoryLoading}
            versionHistoryError={versionHistoryError}
            isDraftOnly={!currentVersionId}
            onOpenRule={openRuleFromField}
            pendingOpenRuleId={pendingOpenRuleId}
            onPendingOpenRuleConsumed={() => setPendingOpenRuleId(null)}
            publishedFieldKeys={publishedFieldKeys}
            publishedDef={publishedDef}
          />
        )
        const previewPane = (
          <div className="h-full overflow-hidden bg-background">
            <FormPreviewPane
              definition={definition}
              title={formTitle ? `پیش‌نمایش: ${formTitle}` : 'پیش‌نمایش'}
              banner="پیش‌نمایش زنده — هم‌گام با ویرایش‌های شما (داده ذخیره نمی‌شود)"
              className="h-full"
            />
          </div>
        )
        const assistantPane = (
          <div className="h-full">
            <AssistantPanel
              formId={formId ?? ''}
              formKey={formKey}
              formTitle={formTitle}
              instanceType={instanceType}
              publishedVersionLabel={publishedVersionLabel}
              definition={definition}
              selectedFieldKey={selectedFieldKey}
              onApply={(next) => { setDefinition(next); setIsDirty(true) }}
              onClose={() => setAssistantOpen(false)}
            />
          </div>
        )

        let layout: React.ReactNode
        if (previewOpen && assistantOpen) {
          layout = (
            <ResizableSplit
              storageKey="form-editor-main-split"
              defaultLeftPct={50}
              minPct={20}
              className="flex-1"
              left={editorPane}
              right={
                <ResizableSplit
                  storageKey="form-editor-side-split"
                  defaultLeftPct={50}
                  minPct={25}
                  className="h-full"
                  left={previewPane}
                  right={assistantPane}
                />
              }
            />
          )
        } else if (previewOpen) {
          layout = (
            <ResizableSplit
              storageKey="form-editor-preview-split"
              defaultLeftPct={62}
              minPct={28}
              className="flex-1"
              left={editorPane}
              right={previewPane}
            />
          )
        } else if (assistantOpen) {
          layout = (
            <ResizableSplit
              storageKey="form-editor-assistant-split"
              defaultLeftPct={62}
              minPct={28}
              className="flex-1"
              left={editorPane}
              right={assistantPane}
            />
          )
        } else {
          layout = editorPane
        }
        return (
          <div className="flex-1 overflow-hidden flex flex-col">{layout}</div>
        )
      })()}

      {/* ── Delete field confirmation dialog ──────────────────────────────── */}
      <Dialog open={!!deletingFieldKey} onOpenChange={(open) => { if (!open) setDeletingFieldKey(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>حذف فیلد؟</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            فیلد «<span className="font-medium text-foreground">{deletingField?.label.fa || deletingFieldKey}</span>» حذف می‌شود و قابل بازیابی نیست.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingFieldKey(null)}>انصراف</Button>
            <Button variant="destructive" onClick={() => deletingFieldKey && deleteField(deletingFieldKey)}>
              حذف
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Publish confirm dialog ─────────────────────────────────────────── */}
      <Dialog open={publishConfirmOpen} onOpenChange={(open) => {
        if (!open) { setUserDecisions(new Map()); setSuperUserPhrase('') }
        setPublishConfirmOpen(open)
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {publishPreview && !currentVersionId
                ? 'انتشار اولیه فرم (v1.0)'
                : (() => {
                    const isActualBreaking =
                      publishPreview?.classified.some((c) => c.classification === 'breaking') ||
                      (publishPreview?.classified ?? []).some((c) => {
                        if (c.classification !== 'breaking_pending_data_check') return false
                        return (publishPreview?.impactData?.find(
                          (d) => d.field_key === (c.item.fieldKey ?? '') && d.change_type === c.item.type
                        )?.affected_count ?? 0) > 0
                      })
                    return isActualBreaking
                      ? `انتشار نسخه اصلی جدید (major bump)`
                      : `انتشار نسخه فرعی جدید (minor bump)`
                  })()
              }
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 text-sm">
            {/* Safe / cosmetic changes — collapsible summary */}
            {publishPreview && (() => {
              const safe = publishPreview.classified.filter(
                (c) => c.classification === 'safe' || c.classification === 'cosmetic_runtime'
              )
              if (safe.length === 0) return null
              return (
                <details className="rounded border border-border">
                  <summary className="px-3 py-2 text-xs text-muted-foreground cursor-pointer select-none flex items-center gap-2">
                    <ChevronDown className="w-3 h-3" />
                    {safe.length} تغییر ایمن / محاسباتی (بدون اثر بر داده‌ها)
                  </summary>
                  <div className="px-3 pb-2 space-y-1 border-t border-border mt-0">
                    {safe.map((c, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground py-0.5">
                        <span className={cn('w-1.5 h-1.5 rounded-full shrink-0',
                          c.classification === 'cosmetic_runtime' ? 'bg-blue-400' : 'bg-emerald-500')} />
                        {c.label}
                        {c.detail && <span className="opacity-60">({c.detail})</span>}
                      </div>
                    ))}
                  </div>
                </details>
              )
            })()}

            {/* Breaking / pending changes — one card per change with decision radio */}
            {publishPreview && (() => {
              const breaking = publishPreview.classified.filter((c) => {
                if (c.classification === 'breaking') return true
                if (c.classification === 'breaking_pending_data_check') {
                  const impact = publishPreview.impactData?.find(
                    (d) => d.field_key === (c.item.fieldKey ?? '') && d.change_type === c.item.type
                  )
                  return (impact?.affected_count ?? 0) > 0
                }
                return false
              })
              if (breaking.length === 0) return null

              return (
                <div className="space-y-3">
                  <p className="text-xs font-semibold text-destructive">
                    {breaking.length} تغییر شکننده — نحوه برخورد با داده‌های موجود را انتخاب کنید:
                  </p>
                  {breaking.map((c) => {
                    const id = diffItemId(c.item)
                    const dec = userDecisions.get(id) ?? { choice: 'grandfather' as DecisionChoice, migration_mapping: {} }
                    const impact = publishPreview.impactData?.find(
                      (d) => d.field_key === (c.item.fieldKey ?? '') && d.change_type === c.item.type
                    )
                    const removedOptKey = c.item.details?.optionKey as string | undefined
                    // Remaining options in new definition for migration mapping
                    const newField = definition.fields.find((f) => f.field_key === c.item.fieldKey)
                    const remainingOptions = (newField?.type_config?.options as Array<{ option_key: string; label?: { fa?: string } }> | undefined) ?? []

                    const setChoice = (choice: DecisionChoice) =>
                      setUserDecisions((prev) => new Map(prev).set(id, { ...dec, choice }))
                    const setMapping = (key: string, val: string) =>
                      setUserDecisions((prev) => {
                        const next = new Map(prev)
                        const existing = next.get(id) ?? { choice: 'migrate' as DecisionChoice, migration_mapping: {} }
                        next.set(id, { ...existing, migration_mapping: { ...existing.migration_mapping, [key]: val } })
                        return next
                      })

                    return (
                      <div key={id} className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-xs font-medium text-destructive">{c.label}</p>
                            {c.detail && <p className="text-[10px] text-muted-foreground">{c.detail}</p>}
                          </div>
                          {impact && (
                            <div className="text-[10px] text-orange-400 text-end shrink-0">
                              <div>{impact.affected_count.toLocaleString('fa-IR')} سابقه تأثیر می‌گیرند</div>
                              {impact.approved_count > 0 && (
                                <div>{impact.approved_count.toLocaleString('fa-IR')} تأیید‌شده</div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Decision radio buttons */}
                        <div className="flex flex-wrap gap-1.5 pt-1">
                          {(
                            [
                              { val: 'grandfather' as DecisionChoice, label: 'حفظ بدون تغییر', hint: 'داده‌های قدیمی دست‌نخورده باقی می‌مانند' },
                              { val: 'migrate' as DecisionChoice, label: 'مهاجرت داده', hint: 'مقادیر قدیمی به مقادیر جدید تبدیل می‌شوند' },
                              { val: 'mark_for_revision' as DecisionChoice, label: 'بازبینی الزامی', hint: 'سابقه‌های مرتبط به وضعیت «نیاز به بازبینی» تغییر می‌یابند' },
                            ] as const
                          ).map(({ val, label, hint }) => (
                            <button
                              key={val}
                              type="button"
                              onClick={() => setChoice(val)}
                              title={hint}
                              className={cn(
                                'px-2.5 py-1 rounded text-[11px] border transition-colors',
                                dec.choice === val
                                  ? 'bg-primary text-primary-foreground border-primary'
                                  : 'border-border text-muted-foreground hover:border-primary/50 hover:text-foreground'
                              )}
                            >
                              {label}
                            </button>
                          ))}
                        </div>

                        {/* Migrate option_removed: per-key mapping */}
                        {dec.choice === 'migrate' && c.item.type === 'option_removed' && removedOptKey && (
                          <div className="pt-1 space-y-1">
                            <p className="text-[10px] text-muted-foreground">
                              گزینه «{c.item.details?.optionLabel as string ?? removedOptKey}» را به چه گزینه‌ای تبدیل کنیم؟
                            </p>
                            <Select
                              value={dec.migration_mapping[removedOptKey] ?? ''}
                              onValueChange={(v) => setMapping(removedOptKey, v)}
                            >
                              <SelectTrigger className="h-7 text-xs">
                                <SelectValue placeholder="انتخاب گزینه مقصد (اختیاری — خالی = حذف مقدار)" />
                              </SelectTrigger>
                              <SelectContent>
                                {remainingOptions.map((opt) => (
                                  <SelectItem key={opt.option_key} value={opt.option_key} className="text-xs">
                                    {opt.label?.fa ?? opt.option_key}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )
            })()}

            {/* Initial publish notice */}
            {publishPreview && !currentVersionId && (
              <p className="text-xs text-muted-foreground">اولین انتشار — نسخه 1.0 ایجاد می‌شود.</p>
            )}

            {/* Super-user confirmation for instance_type change */}
            {hasInstanceTypeChange && (
              <div className="rounded-lg border border-red-500/50 bg-red-500/10 p-3 space-y-2">
                <div className="flex items-center gap-2 text-destructive text-xs font-semibold">
                  <ShieldAlert className="w-4 h-4" />
                  تأیید Super User لازم است
                </div>
                <p className="text-xs text-muted-foreground">
                  تغییر نوع نمونه‌پذیری فرم یک عملیات غیرقابل‌برگشت است.
                  عبارت «تأیید می‌کنم» را وارد کنید:
                </p>
                <input
                  className="w-full rounded border border-border bg-background px-2 py-1 text-xs"
                  value={superUserPhrase}
                  onChange={(e) => setSuperUserPhrase(e.target.value)}
                  placeholder="تأیید می‌کنم"
                  dir="rtl"
                />
              </div>
            )}

            {publishMutation.error && (
              <p className="text-destructive text-xs">{(publishMutation.error as Error).message}</p>
            )}
          </div>

          <DialogFooter className="shrink-0 pt-2 border-t border-border">
            <Button variant="outline" onClick={() => setPublishConfirmOpen(false)}>انصراف</Button>
            <Button
              onClick={() => publishMutation.mutate()}
              disabled={
                publishMutation.isPending ||
                (hasInstanceTypeChange && superUserPhrase !== 'تأیید می‌کنم')
              }
              variant={publishPreview?.classified.some((c) => c.classification === 'breaking') ? 'destructive' : 'default'}
            >
              <Upload className="w-4 h-4 me-1.5" />
              {publishMutation.isPending ? 'در حال انتشار...' : 'تأیید و انتشار'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Discard draft confirm dialog ───────────────────────────────────── */}
      <Dialog open={discardConfirmOpen} onOpenChange={setDiscardConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>حذف پیش‌نویس؟</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            پیش‌نویس حذف می‌شود و فرم به نسخه منتشرشده
            {publishedVersionLabel ? ` (${publishedVersionLabel})` : ''} بازمی‌گردد.
            این عمل قابل بازگشت نیست.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscardConfirmOpen(false)}>انصراف</Button>
            <Button
              variant="destructive"
              onClick={() => discardMutation.mutate()}
              disabled={discardMutation.isPending}
            >
              {discardMutation.isPending ? 'در حال حذف...' : 'حذف پیش‌نویس'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Draft conflict dialog ──────────────────────────────────────────── */}
      <Dialog open={draftConflictOpen} onOpenChange={setDraftConflictOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>تعارض پیش‌نویس</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            کاربر دیگری پیش‌نویس را ویرایش کرده است. صفحه را بارگذاری کنید.
            تغییرات ذخیره‌نشده شما در clipboard کپی می‌شود.
          </p>
          <DialogFooter>
            <Button
              onClick={() => {
                navigator.clipboard?.writeText(JSON.stringify(definition, null, 2)).catch(() => {})
                setDraftConflictOpen(false)
                qc.invalidateQueries({ queryKey: ['form-version', formId] })
              }}
            >
              کپی و بارگذاری مجدد
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
